import {boardCellKey, type CellAddress} from '@github-ui/board-core'

import {normalizeItemMoves} from './board-move-axes'
import type {SavedViewsBoardCellPage, SavedViewsBoardItem, SavedViewsBoardPayload} from './types'

/**
 * A board holding one cell's first page in place of the cards it had, and the cursor that page
 * resumes from. Every other cell keeps its identity.
 *
 * The cell keeps its `totalCount`, because a cell page carries no count. A cell the payload never
 * sent is an empty pair, so it is added when its column and row are loaded, with no count. On an
 * axis the board has not loaded, the page is left out, the way `appendCellPage` leaves it out.
 */
export function replaceCellPage<TItem extends SavedViewsBoardItem, TMetadata>(
  payload: SavedViewsBoardPayload<TItem, TMetadata>,
  address: CellAddress,
  page: SavedViewsBoardCellPage<TItem>,
): SavedViewsBoardPayload<TItem, TMetadata> {
  const key = boardCellKey(address)
  if (boardCellKey(page) !== key) return payload
  const {data, cellCursors, rowField} = payload.boardData

  const items = page.items.map(item => normalizeItemMoves(item, rowField != null))
  let replaced = false
  const cells = data.cells.map(cell => {
    if (boardCellKey(cell) !== key) return cell

    replaced = true
    return {...cell, items, itemsLoaded: true, hasMoreItems: page.hasMoreItems}
  })

  if (!replaced) {
    const isDrawn =
      data.columns.some(column => column.key === address.columnKey) && data.rows.some(row => row.key === address.rowKey)
    if (!isDrawn) return payload
    cells.push({
      columnKey: address.columnKey,
      rowKey: address.rowKey,
      items,
      itemsLoaded: true,
      hasMoreItems: page.hasMoreItems,
    })
  }

  const cursors = new Map(cellCursors)
  if (page.itemsCursor == null) cursors.delete(key)
  else cursors.set(key, page.itemsCursor)

  return {
    ...payload,
    boardData: {...payload.boardData, data: {...data, cells}, cellCursors: cursors},
  }
}

/**
 * A reloaded page's cards, with each card the cell already holds kept as the cell holds it, apart
 * from whether it is ranked. The page decides the order, so no card it places is guessed.
 *
 * A page comes from the search index, which can trail an edit the board has already folded in,
 * so a held card is kept over the page's copy, as `appendCellPage` keeps it.
 */
export function withHeldCopies<TItem extends SavedViewsBoardItem>(
  page: readonly TItem[],
  held: readonly TItem[],
): TItem[] {
  const copies = new Map(held.map(card => [card.globalId, card]))
  return page.map(card => {
    const copy = copies.get(card.globalId)
    if (copy === undefined) return card
    if ((copy.ranked === true) === (card.ranked === true) && copy.positionGuessed !== true) return copy
    return {...copy, ranked: card.ranked, ...(copy.positionGuessed === true && {positionGuessed: false})}
  })
}
